import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams, Link } from "react-router-dom";

// Material UI
import { Grid, Typography, Paper, Button, Divider, IconButton } from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VisibilityIcon from '@mui/icons-material/Visibility';

// Components
import { Spinner } from '@arredo/reactjs-ui';

// Hooks
import useInternalVoucher from "../../../hooks/internalVouchers/useInternalVoucher";
import useNotify from '../../../hooks/notify';

// Service
import PaymentService from '../../../services/paymentService';

// Other
import { cashTypesFormat, cardTypeFormat, cashTypes } from "../../../models/CashRegisterMovementEntity";
import useStyles from "../styles";
import { utils } from "../../../utils/utils";

const FormControll = ({ label, value }) =>
    <Grid container alignItems='center' justifyContent='space-between'>
        <Grid size={4}>
            <Typography color='primary' variant='subtitle1' component='p'>
                <strong>{label}:</strong>
            </Typography>
        </Grid>
        <Grid container justifyContent='flex-end' size={8}>
            <Typography variant='body1' data-testid={label}>{value}</Typography>
        </Grid>
    </Grid>

const PaymentDetail = ({ title, cashType, bin, lastCardDigits, planId, bankId, cuponNumber, currency, amount, isDestination }) => {
    const { classes } = useStyles();
    const { Notify, variants } = useNotify();
    const [bankPlan, setBankPlan] = useState()

    useEffect(() => {
        if (planId && bankId) {
            PaymentService
                .GetBankPlan(planId, bankId)
                .then(res => setBankPlan(res))
                .catch(() => Notify(`No se encontro el plan ${planId} y el banco ${bankId}`, variants.ERROR))
        }
    }, [planId, bankId, Notify, variants])

    const GetTypeCard = () =>
        `${bankPlan.card?.name || ''} ${bankPlan.card?.type ? cardTypeFormat[bankPlan.card.type] : ''}`

    return (
        <Paper className={classes.paper}>
            <Grid container spacing={1}>
                <Grid size={12}>
                    <Typography variant='h5' align='center'>
                        {title}
                    </Typography>
                </Grid>
                <Grid size={12}>
                    <Divider />
                </Grid>
                <Grid size={12}>
                    <FormControll label='Medio de pago' value={cashTypesFormat[cashType]} />
                    <FormControll label='Moneda' value={currency} />
                    {
                        cashType === cashTypes.cash &&
                        <FormControll label='Importe' value={isDestination ? `~${utils.formatMoney(amount)}` : utils.formatMoney(amount)} />
                    }
                    {
                        bankPlan &&
                        <>
                            <FormControll label='Primeros 6 digitos' value={bin} />
                            <FormControll label='Ultimos 4 digitos' value={utils.zeroPad(lastCardDigits, 4)} />
                            <FormControll label='Banco' value={bankPlan.bank.name} />
                            <FormControll label='Tarjeta' value={GetTypeCard()} />
                            <FormControll label='Plan' value={bankPlan.plan.name} />
                        </>
                    }
                    {
                        !!cuponNumber &&
                        <FormControll label='Numero de Cupon' value={cuponNumber} />
                    }
                </Grid>
            </Grid>
        </Paper>
    );
}

const DetailInternalVoucher = () => {
    const { ivId } = useParams();
    const { classes } = useStyles();
    const { internalVoucher, loading } = useInternalVoucher(ivId);
    const history = useNavigate();
    const location = useLocation();

    const goBack = () =>
        history(`${location.state?.from?.indexOf('internalVouchers?') > -1 ? location.state.from : "/internalVouchers"}`);

    const GetPayment = (type = 'Origin') => {
        return ({
            cashType: internalVoucher[`cashType${type}`],
            bin: internalVoucher[`bin${type}`],
            lastCardDigits: internalVoucher[`lastCardDigits${type}`],
            planId: internalVoucher[`planId${type}`],
            bankId: internalVoucher[`bankId${type}`],
            cuponNumber: internalVoucher[`cuponNumber${type}`],
            currency: internalVoucher[`currency${type}`],
            amount: type === 'Origin' ? internalVoucher.amount : internalVoucher.amountDestination,
            conversionRate: internalVoucher.conversionRate
        })
    }

    return (
        <>
            {loading && <Spinner lockScreen />}
            <Grid container spacing={1} alignItems='flex-start'>
                <Grid size={12}>
                    <Paper className={classes.paper}>
                        <Grid container spacing={1} alignItems='center' alignContent='center'>
                            <Grid>
                                <IconButton color='primary' onClick={goBack} size="large">
                                    <ArrowBackIcon />
                                </IconButton>
                            </Grid>
                            <Grid size="grow">
                                <Typography variant='h5' component='h4'>
                                    Detalle del comprobante interno
                                </Typography>
                            </Grid>
                            <Grid>
                                <Button
                                    variant='outlined'
                                    component={Link}
                                    to={`/orders/detail/${internalVoucher?.orderId}`}
                                    startIcon={<VisibilityIcon />}
                                >
                                    Ver Venta
                                </Button>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>
                <Grid
                    size={{
                        xs: 12,
                        sm: 4
                    }}
                >
                    <Paper className={classes.paper}>
                        <Grid container spacing={2}>
                            <Grid size={12}>
                                <Typography variant='h5'>
                                    Detalle General
                                </Typography>
                            </Grid>
                            <Grid size={12}>
                                <Divider />
                            </Grid>
                            <Grid size={12}>
                                <FormControll label='Caja' value={internalVoucher.cashRegisterName} />
                                <FormControll label='Importe' value={`${internalVoucher.currencyOrigin} ${utils.formatMoney(internalVoucher?.amount)}`} />
                                <FormControll label='Nro. Factura' value={internalVoucher.invoiceCode} />
                                <FormControll label='Usuario' value={internalVoucher.userEmail} />
                                <FormControll label='Fecha creado' value={internalVoucher?.created ? utils.formatDate(internalVoucher.created) : ''} />
                                <Typography color='primary' variant='subtitle1' component='p'>
                                    <strong>Observación:</strong>
                                </Typography>
                                <Typography variant='body1'>
                                    {internalVoucher.observation}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>
                <Grid
                    size={{
                        xs: 12,
                        sm: 8
                    }}
                >
                    <Grid container spacing={1}>
                        <Grid
                            size={{
                                xs: 12,
                                xl: 6
                            }}
                        >
                            <PaymentDetail title='Origen' {...GetPayment('Origin')} />
                        </Grid>
                        <Grid
                            size={{
                                xs: 12,
                                xl: 6
                            }}
                        >
                            <PaymentDetail title='Destino' {...GetPayment('Destination')} isDestination />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </>
    );
}
export default DetailInternalVoucher;